import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { combineLatest } from 'rxjs';
import { delay, filter } from 'rxjs/operators';

// PrimeNG Imports
import { DrawerModule } from 'primeng/drawer';
import { DialogService } from 'primeng/dynamicdialog';
import { PanelModule } from 'primeng/panel';
import { ButtonModule } from 'primeng/button';
import { AuthUserService } from './core/services/auth-user.service';
import { ResponsiveService } from './core/services/responsive.service';
import { SideBarToggleService } from './core/services/side-bar-toggle.service';
import { environment } from '../environments/environment';
import { TitleService } from './core/services/title.service';
import { publicRoutes } from './core/common-enums/enum';
import { SharedModuleService } from './core/services/shared-module.service';
import { SidebarComponent } from './layouts/components/sidebar/sidebar.component';
import { NavbarComponent } from './layouts/components/navbar/navbar.component';
import { SubdomainService } from './core/services/subdomain.service';
import { LoaderComponentComponent } from './layouts/components/loader-component/loader-component.component';
import { BreadcrumbComponent } from './layouts/components/breadcrumb/breadcrumb.component';
import { ConfirmationPopupComponent } from './layouts/components/confirmation-popup/confirmation-popup.component';
import { HMRStatusComponent } from './shared/hmr-status/hmr-status.component';
import { WalkthroughService } from './core/services/walkthrough.service';
import { SessionTimeoutService } from './core/services/session-timeout.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterOutlet,
    DrawerModule,
    PanelModule,
    ButtonModule,
    SidebarComponent,
    NavbarComponent,
    LoaderComponentComponent,
    BreadcrumbComponent,
    ConfirmationPopupComponent,
    HMRStatusComponent
  ],
  providers: [DialogService]
})
export class AppComponent implements OnInit {
  readonly title = environment.appTitle;
  readonly mobile: boolean;
  flag: boolean = false;
  isLoggedIn = false;
  showSideBar: boolean = true;
  firstTimeLogin!: boolean;
  isExpanded!: boolean;
  readonly showConfirmModal = signal(false);
  isRoleAdmin: boolean = false;
  subdomain: string | null;
  // Services injected using inject() function as per Angular 19 best practices
  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly _responsiveService = inject(ResponsiveService);
  private readonly authUser = inject(AuthUserService);
  private readonly router = inject(Router);
  private readonly observer = inject(BreakpointObserver);
  private readonly sideBarToggleService = inject(SideBarToggleService);
  private readonly sessionTimeoutService = inject(SessionTimeoutService);
  private readonly titleService = inject(TitleService);
  private readonly sharedModuleService = inject(SharedModuleService);
  private readonly subdomainService = inject(SubdomainService);
  private readonly walkthroughService = inject(WalkthroughService);

  constructor() {
    this.mobile = false;
    this.initializeSubscriptions();
  }

  ngOnInit() {
    // Use the subdomain service instead of direct sessionStorage manipulation
    this.subdomain = this.subdomainService.getSubdomain();
    this.getOrgLogo(this.subdomain);
    this.authUser.getToken();

    this.onResize();
    this.titleService.setTitle();
  }

  /**
   * Toggle the sidebar expansion state
   */
  toggleSidebar(): void {
    if (this.isLoggedIn && this.showSideBar) {
      // Toggle the expansion state
      this.sideBarToggleService.sendIsExpandedUpdate(!this.isExpanded);
    }
  }

  /**
   * Show the session expired modal.
   */
  openLogoutModal(): void {
    // Close any active walkthrough before showing session expired modal
    this.walkthroughService.exitWalkthrough();
    this.showConfirmModal.set(true);
  }

  /**
   * Close modal and redirect to login page
   */
  handleModalClose(): void {
    this.showConfirmModal.set(false);
    // Ensure walkthrough is fully closed before navigating away
    this.walkthroughService.exitWalkthrough();
    this.logout();
  }

  /**
   * Logs out the user from the application and clears the local storage
   */
  logout(): void {
    // Final safety: remove any lingering walkthrough UI before navigating
    this.walkthroughService.exitWalkthrough();
    localStorage.clear();
    this.authUser.isLoggedIn.set(false);
    this.router.navigate(['/login']);
    // Close any open PrimeNG dialogs if needed
    // this.dialogService.closeAll();
  }

  /** Initialize component subscriptions with component lifecycle cleanup. */
  private initializeSubscriptions(): void {
    this.authUser
      .getSideBarFlag()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: boolean) => {
        this.flag = res;
      });

    this.sessionTimeoutService.sessionExpired$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.openLogoutModal());

    this.authUser.isLoggedInObservable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: boolean) => {
        this.handleLoginStateChange(res);
      });

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this.handleNavigationEnd(event);
      });

    this.authUser
      .getUserDetailBehaviorSubject()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (data) {
          const userRole = data.newRoleFinal?.roleDescription;
          if (userRole === 'BROKER ADMIN' || userRole === 'ADVISOR') {
            this.firstTimeLogin = data.brokerAdminfirstTimeLogin;
            this.showSideBar = !this.firstTimeLogin;
          } else {
            this.showSideBar = true;
          }
          this.isRoleAdmin = userRole === 'ADMIN';
          this.changeDetectorRef.markForCheck();
        }
      });

    this.sideBarToggleService
      .getIsExpandedUpdate()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: { text: boolean }) => {
        this.isExpanded = res?.text;
        this.changeDetectorRef.markForCheck();
      });

    combineLatest([
      this.authUser.isLoggedInObservable,
      this.observer.observe(['(max-width: 768px)']).pipe(delay(1))
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([isLoggedIn, check]) => {
        if (isLoggedIn) {
          this.sideBarToggleService.sendIsExpandedUpdate(!check.matches);
        }
      });
  }

  /** Update login-dependent idle handling and activity listeners. */
  private handleLoginStateChange(isLoggedIn: boolean): void {
    this.isLoggedIn = isLoggedIn;
    this.sessionTimeoutService.setLoggedIn(isLoggedIn);
    this.changeDetectorRef.markForCheck();
  }

  /** Hide authenticated chrome when navigation reaches a public route. */
  private handleNavigationEnd(event: NavigationEnd): void {
    const currentRoute = event.urlAfterRedirects || event.url;
    const shouldHide = publicRoutes.includes(currentRoute);
    if (shouldHide) {
      this.isLoggedIn = false;
      this.showSideBar = false;
      // Safety net: forcibly remove any lingering walkthrough UI on public pages
      this.walkthroughService.exitWalkthrough();
      this.changeDetectorRef.markForCheck();
    }
  }

  onResize(): void {
    this._responsiveService.checkWidth();
  }
  getOrgLogo(domain: string | null): void {
    //Fallback
    if (!domain) {
      this.sharedModuleService.setLogoData({
        img: 'assets/images/gemfin-logo.svg',
        tagline: 'Most Trusted Investment Portal',
        base64Image: ''
      });
    } else {
      this.sharedModuleService
        .getOrgLogoDetails(domain)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            const { logoUrl, slogan, base64Image } = res || {};

            const logoData = {
              img: logoUrl || 'assets/images/gemfin-logo.svg',
              tagline: slogan?.trim() || 'Most Trusted Investment Portal',
              base64Image: base64Image || ''
            };

            this.sharedModuleService.setLogoData(logoData);
          },
          error: () => {
            this.sharedModuleService.setLogoData({
              img: 'assets/images/gemfin-logo.svg',
              tagline: 'Most Trusted Investment Portal',
              base64Image: ''
            });
          }
        });
    }
  }
}
